import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@components/common/table"
import { Badge } from "@components/common/badge"
import { Button } from "@components/common/button"
import { Avatar, AvatarImage } from "@components/common/avatar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import axios from "axios"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { useQuery } from "@tanstack/react-query"
import {
  FINANCIAL_SERVER_URL,
  ORGANIZATION_PROJECT_ROLES_ENDPOINT,
  GET_FINANCIAL_PROJECT_ROLE_ENDPOINT,
} from "@pma/utils/api-routes"
import { useAuth } from "@pma/stores/auth-store"
import { DictType } from "@pma/types"
import useTranslator from "@pma/hooks/use-translator"
import PmsLoader from "@components/common/loader"
import AssignProjectRole from "./assign-project-role"
import DeleteProjectUserRole from "./delete-project-user-role"
import { useNavigate } from "react-router-dom"
import AssignProjectRoleWithoutProjectId from "./assign-project-role-without-project-id"
import { SETTINGS_PAGE_ROUTE } from "@pma/utils/page-routes"
import clsx from "clsx"

interface Props {
  projectId?: string
}

const ProjectFinancialRoles = ({ projectId }: Props) => {
  const { authConfig, setTabManager } = useAuth()
  const { translate, locale } = useTranslator()

  const navigate = useNavigate()

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const organizationId = authConfig?.last_organization_id

  const getRoles = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + ORGANIZATION_PROJECT_ROLES_ENDPOINT,
      {
        organization_id: organizationId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const getProjectRoles = async () => {
    const response = await axios.get(
      FINANCIAL_SERVER_URL + GET_FINANCIAL_PROJECT_ROLE_ENDPOINT + `/${projectId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["project-roles", projectId],
    queryFn: projectId ? getProjectRoles : getRoles,
  })

  const groupPermissions = (roles: DictType[]) => {
    const temp: DictType[] = []
    roles?.forEach((role) => {
      role.user = flattenRoleUsers(role)
      temp.push(role)
    })

    return temp
  }

  function flattenRoleUsers(role: any) {
    const flattenedUsers: DictType = {}
    if (projectId) {
      const user = role?._user

      flattenedUsers[user?.id] = {
        _user: user,
      }

      return flattenedUsers
    } else {
      role._role_users.forEach((user: DictType) => {
        if (!flattenedUsers[user.user_id]) {
          flattenedUsers[user.user_id] = {
            _user: user._user,
            projects: [
              {
                role: user?.id,
                id: user.project_id,
              },
            ],
          }
        } else {
          flattenedUsers[user.user_id].projects.push({
            role: user?.id,
            id: user.project_id,
          })
        }
      })
    }

    return Object.values(flattenedUsers)
  }

  const organizationGroupedPermissions = groupPermissions(data)
  const filteredRoles: DictType[] = projectId ? data : organizationGroupedPermissions

  return (
    <Card className='w-full'>
      <CardHeader className='space-y-4 p-4 sm:p-6'>
        <CardTitle className='text-lg sm:text-xl md:text-2xl'>
          {translate("Financial Roles")}
        </CardTitle>
        <CardDescription>
          <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-sm sm:text-base'>
              {translate("Assign financial roles to members in project")}
            </p>
            {projectId && (
              <Button
                variant='link'
                onClick={() => {
                  setTabManager({ group: "settings", main: "roles", sub: "finance" })
                  navigate(SETTINGS_PAGE_ROUTE)
                }}
                size='sm'
                className='w-fit'
              >
                {translate("Organization Roles")}
              </Button>
            )}
          </div>
        </CardDescription>
      </CardHeader>

      <CardContent className='p-4 sm:p-6'>
        {isLoading ? (
          <PmsLoader />
        ) : (
          <div className='-mx-4 overflow-x-auto sm:mx-0 sm:overflow-x-visible'>
            <Table>
              <TableHeader>
                <TableRow className='hover:bg-transparent'>
                  <TableHead
                    className={clsx(
                      "py-2 text-xs sm:py-3 sm:text-sm md:text-base",
                      isHebrew ? "text-right" : "text-left",
                    )}
                  >
                    {translate("Role Name")}
                  </TableHead>
                  <TableHead
                    className={clsx(
                      "hidden py-2 text-xs sm:table-cell sm:py-3 sm:text-sm md:text-base",
                      isHebrew ? "text-right" : "text-left",
                    )}
                  >
                    {translate("Permissions")}
                  </TableHead>
                  <TableHead
                    className={clsx(
                      "py-2 text-xs sm:py-3 sm:text-sm md:text-base",
                      isHebrew ? "text-right" : "text-left",
                    )}
                  >
                    {translate("Users")}
                  </TableHead>
                  <TableHead
                    className={clsx(
                      "py-2 text-xs sm:py-3 sm:text-sm md:text-base",
                      isHebrew ? "text-right" : "text-left",
                    )}
                  >
                    {translate("Actions")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoles?.map((role: DictType, index: number) => {
                  const user = projectId ? role?._users : role?.user
                  const roleObject = role
                  const roleName = roleObject?.name || roleObject?._role?.name
                  const isContractor = roleName === "Constructor"

                  return (
                    <TableRow key={index} className='transition-colors hover:bg-slate-50'>
                      {/* Role Name */}
                      <TableCell className='px-2 py-2 text-xs font-medium sm:px-4 sm:py-3 sm:text-sm md:text-base'>
                        <span>
                          {isContractor ? (isHebrew ? "קבלן" : "Contractor") : translate(roleName)}
                        </span>
                      </TableCell>

                      {/* Permissions - Hidden on mobile */}
                      <TableCell className='hidden px-2 py-2 text-xs sm:table-cell sm:px-4 sm:py-3 sm:text-sm md:text-base'>
                        <div className='flex flex-wrap gap-1'>
                          {!roleObject?.constructor &&
                          !roleObject?.fin_manager &&
                          !roleObject?.supervisor &&
                          !roleObject?.designer ? (
                            <span className='text-xs text-muted-foreground'>
                              {translate("No permissions")}
                            </span>
                          ) : (
                            <>
                              {roleObject?.constructor && (
                                <Badge variant='outline' className='text-xs'>
                                  {translate("Contractor")}
                                </Badge>
                              )}
                              {roleObject?.designer && (
                                <Badge variant='default' className='text-xs capitalize'>
                                  {translate("Planner")}
                                </Badge>
                              )}
                              {roleObject?.fin_manager && (
                                <Badge variant='premium' className='text-xs capitalize'>
                                  {translate("Financial Manager")}
                                </Badge>
                              )}
                              {roleObject?.supervisor && (
                                <Badge variant='secondary' className='text-xs capitalize'>
                                  {translate("Supervisor")}
                                </Badge>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>

                      {/* Users */}
                      <TableCell className='px-2 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm md:text-base'>
                        <div className='flex min-w-0 flex-wrap gap-1'>
                          {user?.length < 1 ? (
                            <span className='text-muted-foreground'>---</span>
                          ) : (
                            user?.map((u: DictType, idx: number) => (
                              <DeleteProjectUserRole
                                user={{ ...u, role }}
                                refetch={refetch}
                                key={idx}
                                projectId={ projectId}
                              >
                                <div className='flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white p-2 text-xs shadow-sm transition hover:shadow-md sm:text-sm'>
                                  <div className='flex min-w-0 items-center gap-2'>
                                    <Avatar className='h-5 w-5 flex-shrink-0 sm:h-6 sm:w-6'>
                                      <AvatarImage
                                        src={
                                          u?._user?.user_picture?.url ||
                                          u?.user_picture?.url ||
                                          `${process.env.PUBLIC_URL}/user-profile.svg`
                                        }
                                      />
                                    </Avatar>
                                    <span className='truncate font-medium text-gray-800'>
                                      {u?._user?.name || u?.name}
                                    </span>
                                  </div>
                                </div>
                              </DeleteProjectUserRole>
                            ))
                          )}
                        </div>
                      </TableCell>

                      {/* Actions */}
                      <TableCell className='px-2 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm md:text-base'>
                        <div className='flex items-center justify-center'>
                          {projectId ? (
                            <AssignProjectRole
                              revalidate={refetch}
                              role={role}
                              projectId={projectId}
                            />
                          ) : (
                            <AssignProjectRoleWithoutProjectId revalidate={refetch} role={role} />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <CardFooter className='flex justify-center px-4 py-3 text-xs text-muted-foreground sm:justify-between sm:px-6 sm:py-4 sm:text-sm'>
        <div>
          {filteredRoles?.length} {translate("of")} {data?.length} {translate("roles")}
        </div>
      </CardFooter>
    </Card>
  )
}

export default ProjectFinancialRoles
